/**
 * 客户电话统一格式工具（前端 + 「编辑客户信息 / 新增线索」共用）
 *
 * 需求来源：2026-10-16 市场部反馈 SYS-001
 *   痛点：电话号码可以随意录入，库里混着 6262278888 / 626-227-8888 / 626.227.8888 多种写法；
 *        同一客户还能录入多个电话，保存时不查重，后续拨打/去重/导入都对不上。
 *   目标：① 统一成 NNN-NNN-NNNN；② 每位客户只有主电话这一条（其他联系人各自一条）；
 *        ③ 保存时查重，重复号码提示并阻止保存。
 *
 * 设计取舍：
 *   - 归一化只保留数字并做「去国家码 1」处理，因此 (626) 227-8888 / +1 626 227 8888 都收敛成同一串，
 *     查重才能覆盖各种写法；不足 10 位（如分机、短号）不强行格式化，避免把有效输入改坏。
 *   - 校验只拦「看起来是电话号码但位数不对」的输入；空值放行（电话不是必填项）。
 *   - 纯函数、无依赖，方便单测与后端复用同一口径（后端另有等价校验，见 server.py 的 normalize_phone）。
 */

/** 取出纯数字（用于比较与查重）：去掉所有非数字字符，再去掉北美国家码前缀 1 */
export const phoneDigits = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  // 11 位且以 1 开头 → 去掉国家码（1 626 227 8888 → 6262278888），与 10 位本地号同口径
  return digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
};

/**
 * 格式化电话：能识别成 10 位北美号码时统一输出 NNN-NNN-NNNN；
 * 其它（空值 / 不满 10 位 / 国际号）原样返回，避免误改用户真实输入。
 */
export const formatPhone = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const digits = phoneDigits(raw);
  if (digits.length !== 10) return raw;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};

/** 输入过程中的轻量格式化：边输边补连字符（仍允许用户退格编辑） */
export const formatPhoneInput = (value) => {
  const raw = String(value || "");
  const digits = raw.replace(/\D/g, "").slice(0, 11);
  if (!digits) return "";
  // 只对「看上去就是北美号码」的输入做自动分段；含字母等其它字符时不动，交给保存前校验
  if (/[^\d\s()+\-.]/.test(raw)) return raw;
  const local = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  if (local.length <= 3) return local;
  if (local.length <= 6) return `${local.slice(0, 3)}-${local.slice(3)}`;
  return `${local.slice(0, 3)}-${local.slice(3, 6)}-${local.slice(6, 10)}`;
};

/** 校验：空值合法；非空则必须是 10 位（或 11 位带 1 的北美号）。返回错误文案，合法返回空串。 */
export const validatePhone = (value, zh = true) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/[^\d\s()+\-.]/.test(raw)) return zh ? "电话只能包含数字、空格、括号和连字符" : "Phone may only contain digits, spaces, parentheses and dashes";
  const digits = phoneDigits(raw);
  if (digits.length !== 10) return zh ? "电话需为 10 位美国号码，例如 626-227-8888" : "Phone must be a 10-digit US number, e.g. 626-227-8888";
  return "";
};
